// Sheets' comments adapter. A new comment anchors to the active cell / range
// ("Sheet1!B4", "'Q3 plan'!B4:C6") with its values as the quote; clicking a
// thread goes to that sheet and selects the range.

import type { SpreadsheetComponent } from "@syncfusion/ej2-react-spreadsheet";
import type { CommentAnchor, CommentsAdapter } from "@/features/drive-file/CommentsPanel";

type SheetModel = ReturnType<SpreadsheetComponent["getActiveSheet"]>;

const MAX_QUOTE = 500;
const MAX_ANCHOR = 128;
const MAX_CELLS = 60;
const CELL_RE = /^([A-Z]{1,3})(\d{1,7})$/;

/** "B4" → [row 3, column 1] (zero-based). */
function parseCell(ref: string): [number, number] | null {
    const m = CELL_RE.exec(ref);
    if (!m) return null;
    let col = 0;
    for (const ch of m[1]!) col = col * 26 + (ch.charCodeAt(0) - 64);
    return [Number(m[2]) - 1, col - 1];
}

/** "b4:b4" → "B4", "$A$1:C3" → "A1:C3"; the first of several ranges. Null when it isn't one. */
function normaliseRange(range: string): string | null {
    const first = (range.trim().split(/\s+/)[0] ?? "").toUpperCase().replace(/\$/g, "");
    const [a, b] = first.split(":");
    if (!a || !parseCell(a)) return null;
    if (!b || b === a) return a;
    return parseCell(b) ? `${a}:${b}` : a;
}

/** Excel spelling: bare when simple, else quoted with '' escaping. */
function sheetRef(name: string): string {
    return /^[A-Za-z_][A-Za-z0-9_.]*$/.test(name) ? name : `'${name.replace(/'/g, "''")}'`;
}

function splitAnchor(anchor: string): { sheet: string; range: string } | null {
    const bang = anchor.lastIndexOf("!");
    if (bang <= 0) return null;
    let sheet = anchor.slice(0, bang);
    if (sheet.length >= 2 && sheet.startsWith("'") && sheet.endsWith("'")) sheet = sheet.slice(1, -1).replace(/''/g, "'");
    const range = normaliseRange(anchor.slice(bang + 1));
    return range ? { sheet, range } : null;
}

/** The range's values, row by row ("a, b" per row), for the quote. */
function rangeText(sheet: SheetModel, range: string): string | null {
    const [a, b] = range.split(":");
    const start = a ? parseCell(a) : null;
    const end = b ? parseCell(b) : start;
    if (!start || !end) return null;
    const lines: string[] = [];
    let cells = 0;
    for (let r = Math.min(start[0], end[0]); r <= Math.max(start[0], end[0]) && cells < MAX_CELLS; r++) {
        const values: string[] = [];
        for (let c = Math.min(start[1], end[1]); c <= Math.max(start[1], end[1]) && cells < MAX_CELLS; c++, cells++) {
            const v: unknown = sheet.rows?.[r]?.cells?.[c]?.value;
            values.push(v === undefined || v === null ? "" : String(v).trim());
        }
        while (values.length > 0 && values[values.length - 1] === "") values.pop();
        if (values.length > 0) lines.push(values.join(", "));
    }
    const text = lines.join("\n").trim();
    return text ? text.slice(0, MAX_QUOTE) : null;
}

export function sheetCommentsAdapter(getSheet: () => SpreadsheetComponent | null): CommentsAdapter {
    return {
        selectionHint: "Select a cell or range to comment on it.",

        captureAnchor: (): CommentAnchor | null => {
            const ss = getSheet();
            if (!ss) return null;
            const sheet = ss.getActiveSheet();
            const range = normaliseRange(sheet.selectedRange ?? sheet.activeCell ?? "");
            if (!range) return null;
            const anchor = `${sheetRef(sheet.name ?? "Sheet1")}!${range}`;
            return { anchor: anchor.length <= MAX_ANCHOR ? anchor : range, quote: rangeText(sheet, range) };
        },

        describeAnchor: (anchor) => {
            const parts = splitAnchor(anchor);
            return parts ? `${parts.sheet}!${parts.range}` : null;
        },

        navigate: (anchor) => {
            const ss = getSheet();
            const parts = anchor ? splitAnchor(anchor) : null;
            if (!ss || !parts) return false;
            if (!(ss.sheets ?? []).some((s) => s.name === parts.sheet)) return false;
            try {
                ss.goTo(`${parts.sheet}!${parts.range}`);
            } catch {
                return false;
            }
            // goTo brings the range into view (switching sheets if needed);
            // select it too once that sheet is the active one.
            window.setTimeout(() => {
                try {
                    if (ss.getActiveSheet().name === parts.sheet) ss.selectRange(parts.range);
                } catch {
                    /* the sheet went away meanwhile */
                }
            }, 60);
            return true;
        }
    };
}
